import { Spinner } from "construct-ui";
import moment from "moment";
import SelectCodeMasterCategoryComponent from "../../../../components/code-master/select-code-master-category.component";
import DatePickerComponent from "../../../../components/date-picker/date-picker.component";
import paginationComponent from "../../../../components/pagination/pagination.component";
import { codeMasterType, formats } from "../../../../config/constants";
import BatchJobManagementErrorReportModel from "../../../../models/batch-job-mgmt-error-report/batch-job-mgmt-error-report.model";
import ViewBase from "../../../view-base.view";

/**
 * Error Report
 * 
 * "This page is for admin to see what type of batch job are facing problem during execution and are forced to be cancelled." - LUA Chia Boon 
 * 
 * @author cmcnkhiep
 */

export default function BatchJobManagementErrorReportView() {
  ViewBase.call(this);

  const DESCENDING_TEXT = 'DESC';
  const ASCENDING_TEXT = 'ASC';
  const CANCELLED_STATUS = "CANCELLED";

  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  };
  const DEFAULT_SEARCH_FIELDS = {
    subsidiaryCode: '',
    frequency: '',
    spName: '',
    startDate: moment().format(formats.dateFormat),
    endDate: moment().format(formats.dateFormat),
  };

  let tableData = [];
  let isLoadingTable = false;
  let selectedSortBy = '';
  let selectedOrderBy = '';

  let searchFields = structuredClone(DEFAULT_SEARCH_FIELDS)
  let currentRequestParams = structuredClone(DEFAULT_SEARCH_FIELDS);

  let pagination = structuredClone(DEFAULT_PAGINATION);

  const flipOrderText = (orderText) => {
    return orderText.toUpperCase() === DESCENDING_TEXT ? ASCENDING_TEXT : DESCENDING_TEXT;
  }

  const onSortClick = (orderBy) => {

    if (selectedOrderBy === orderBy) {
      selectedSortBy = flipOrderText(selectedSortBy);
    } else {
      selectedOrderBy = orderBy;
      selectedSortBy = DESCENDING_TEXT;
    }
    if (!tableData.length) {
      return
    }
    getTableData();
  };

  const getSortIcon = (orderBy) => {
    if (selectedOrderBy !== orderBy) {
      return null;
    }

    return selectedSortBy === DESCENDING_TEXT
      ? m('i.bi.bi-sort-down', { "style": "float:left" })
      : m('i.bi.bi-sort-up', { "style": "float:left" });
  };

  let onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    getTableData();
  }

  let onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    getTableData();
  }

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex;
    },

    get pageSize() {
      return pagination.pageSize;
    },

    get orderBy() {
      return selectedOrderBy;
    },

    get sortBy() {
      return selectedSortBy;
    },

    get subsidiaryCode() {
      return currentRequestParams.subsidiaryCode;
    },

    get frequency() {
      return currentRequestParams.frequency;
    },

    get spName() {
      return currentRequestParams.spName.trim();
    },

    get status() {
      return CANCELLED_STATUS;
    },

    get startDate() {
      return moment(currentRequestParams.startDate).format(formats.apiDateFormat);
    },

    get endDate() {
      return moment(currentRequestParams.endDate).format(formats.apiDateFormat);
    },
  }

  function resetSearchFields() {
    searchFields = structuredClone(DEFAULT_SEARCH_FIELDS)
  }

  function updateSearchFields(attr, value) {
    if (Array.isArray(searchFields[attr])) {
      searchFields[attr].splice(0, Infinity, ...value);
    } else {
      searchFields[attr] = value;
    }
  }

  let getTableData = () => {
    isLoadingTable = true;
    currentRequestParams.isExcelDownload = false;
    BatchJobManagementErrorReportModel.searchBatchJobManagementErrorReport(requestParams)
      .then((result) => {
        if (!result) {
          tableData = [];
          return;
        }

        pagination.totalCount = result.total;
        tableData = result.data;
      })
      .catch((error) => {
        tableData = [];
        this.handleError(error);
      })
      .finally(() => {
        isLoadingTable = false;
      })
  }

  let search = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    pagination.pageIndex = DEFAULT_PAGINATION.pageIndex;
    currentRequestParams = structuredClone(searchFields)
    getTableData()

    return false;
  }

  let clearFilters = (e) => {
    e.preventDefault();
    let form = document.querySelector('.needs-validation');
    form.classList.remove('was-validated');
    resetSearchFields();
  }

  return {
    view: (vnode) => {
      return [
        // Search section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('#search-section.card.w-100.mb-0',
            m('.card-body',
              m('form.row.g-4.align-items-end.needs-validation[novalidate]',
                {
                  onsubmit: search
                },
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="dateRangeInput"]', this.getText('LT00000070', 'Date Range')),
                  m(DatePickerComponent, {
                    id: 'dateRangeInput',
                    class: 'form-control',
                    placeholder: this.getText('LT00000070', 'Date Range'),
                    isRange: true,
                    dateStart: searchFields.startDate,
                    dateEnd: searchFields.endDate,
                    onselect: (e) => {
                      updateSearchFields('startDate', e.detail.start);
                      updateSearchFields('endDate', e.detail.end);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6',
                  m('label.form-label[for=subsidiaryCode]', this.getText('LT00000576', 'Subsidiary')),
                  m(SelectCodeMasterCategoryComponent, {
                    id: 'subsidiaryCode',
                    category: codeMasterType.batchJobSubsidiary,
                    isMultiple: false,
                    keyOnly: true,
                    selectedItem: searchFields.subsidiaryCode,
                    onSelect: (value) => {
                      updateSearchFields('subsidiaryCode', value);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6',
                  m('label.form-label[for=frequency]', this.getText('LT00000353', 'Frequency')),
                  m(SelectCodeMasterCategoryComponent, {
                    id: 'frequency',
                    category: codeMasterType.batchJobFreq,
                    isMultiple: false,
                    keyOnly: true,
                    selectedItem: searchFields.frequency,
                    onSelect: (value) => {
                      updateSearchFields('frequency', value);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="spName"]', this.getText("LT00001125", "SP")),
                  m('input#spName.form-control[name="spName"]', {
                    placeholder: this.getText("LT00001125", "SP"),
                    maxLength: 100,
                    value: searchFields.spName,
                    onchange: (e) => {
                      updateSearchFields('spName', e.target.value)
                    }
                  })
                ),
                // Search & clear buttons
                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary[type="submit"]', {
                  }, this.getText("LT00000001", "Search")),
                  m('button.btn.btn-white', {
                    onclick: clearFilters
                  }, this.getText("LT00000031", "Clear")),
                ),
              ),
            )
          ),
        ),
        // Search section - end

        // Table section
        m('.card',
          m('.card-body',
            m('.row.g-2 ',
              m('.col-12.p-0',
                m('.table-responsive',
                  m('table.table', [
                    m('thead.thead-bggrey',
                      m('tr.align-middle',
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('subsidiaryCode'); } },
                          getSortIcon('subsidiaryCode'),
                          this.getText("LT00000576", "Subsidiary")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('jobName'); } },
                          getSortIcon('jobName'),
                          this.getText("LT00001125", "SP")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('frequency'); } },
                          getSortIcon('frequency'),
                          this.getText("LT00000353", "Frequency")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('status'); } },
                          getSortIcon('status'),
                          this.getText("LT00000015", "Status")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          {
                            onclick: () => { onSortClick('remarks'); }
                          },
                          getSortIcon('remarks'),
                          this.getText("LT00000152", "Remarks")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('duration'); } },
                          getSortIcon('duration'),
                          this.getText("LT00001126", "Duration")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('rowCount'); } },
                          getSortIcon('rowCount'),
                          this.getText("LT00001124", "Row Count")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('startDate'); } },
                          getSortIcon('startDate'),
                          this.getText("LT00000301", "Start Date")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('endDate'); } },
                          getSortIcon('endDate'),
                          this.getText("LT00000302", "End Date")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('createdBy'); } },
                          getSortIcon('createdBy'),
                          this.getText("LT00000273", "Created By")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('jobType'); } },
                          getSortIcon('jobType'),
                          this.getText("LT00001127", "Job Type")
                        ),
                      )
                    ),
                    m('tbody',
                      m(Spinner, {
                        active: isLoadingTable,
                        background: true,
                        fill: true,
                        size: 'xl',
                      }),
                      (tableData.length == 0) &&
                      m('tr.align-middle',
                        m('td[colspan=22]', this.getText("EM00000141", "No records found."))
                      ),
                      tableData.length > 0 &&
                      tableData.map((col, index) => [
                        m('tr.align-middle',
                          m('td', col.subsidiaryCode),
                          m('td', col.jobName),
                          m('td', col.frequency),
                          m('td', col.status),
                          m('td.text-wrap', col.remarks),
                          m('td', col.duration),
                          m('td', col.rowCount),
                          m('td', col.startDate),
                          m('td', col.endDate),
                          m('td', col.createdBy),
                          m('td', col.jobType),
                        )
                      ])
                    )
                  ])
                ),
                tableData.length > 0 &&
                m(".px-3",
                  m(paginationComponent, {
                    currentPage: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    totalItems: pagination.totalCount,
                    onPageChanged: onPageChanged,
                    onPageSizeChanged: onPageSizeChanged
                  })
                )
              )
            )
          )
        ),
        // Table section - end

        this.getBaseViews()
      ]
    }
  }
}
